import { computed, ref, toValue, watch } from 'vue'
import { httpsCallable } from 'firebase/functions'
import { functions } from '@/services/firebaseconfig'
import { useUsersStore } from '@/stores/usersStore'
import { useOrganizationStore } from '@/stores/organizationStore'
import { useUserStore } from '@/stores/userStore'
import { useFrontendFlagsStore } from '@/stores/frontendFlagsStore'
import { useToast } from '@/composables/useToast'

export function useTicketWatcherDirectory(submitterUid = null) {
  const users = useUsersStore()
  const orgs = useOrganizationStore()
  const enabled = computed(() => useFrontendFlagsStore().isFlagOn('ticketWatchers'))
  const loading = ref(false)
  const error = ref('')
  async function load() {
    loading.value = true
    error.value = ''
    try {
      await Promise.all([
        users.fetchAllUsers({ force: true }),
        orgs.fetchOrganizations({ force: true }),
      ])
      if (users.error || orgs.error)
        throw new Error('The team directory could not be loaded. Try again.')
    } catch (err) {
      error.value = err.message
    } finally {
      loading.value = false
    }
  }
  watch(
    enabled,
    on => {
      if (on) load()
    },
    { immediate: true }
  )
  const nameFor = uid =>
    users.users[uid]?.identity?.displayName ||
    users.users[uid]?.identity?.email ||
    'Unavailable user'
  const options = computed(() => {
    const internalIds = new Set(
      orgs.organizations.filter(org => org.isInternal === true).map(org => org.id)
    )
    return Object.entries(users.users)
      .filter(([uid, user]) => {
        const rawRoles = user.roles?.userRole || []
        const roles = (Array.isArray(rawRoles) ? rawRoles : [rawRoles]).map(role =>
          String(role).toLowerCase()
        )
        return (
          uid !== toValue(submitterUid) &&
          user.status?.accountStatus === 'active' &&
          !user.linkedCustomerId &&
          !roles.includes('marketing_reviewer') &&
          (user.roles?.isSuperAdmin === true || roles.some(role => role !== 'customer')) &&
          internalIds.has(user.organization?.organizationId)
        )
      })
      .map(([uid]) => ({ value: uid, label: nameFor(uid) }))
      .sort((a, b) => a.label.localeCompare(b.label))
  })
  return { enabled, loading, error, options, nameFor, load }
}

export function useTicketWatchers(report) {
  const user = useUserStore()
  const toast = useToast()
  const directory = useTicketWatcherDirectory(() => toValue(report)?.submitter?.uid)
  const editing = ref(false)
  const saving = ref(false)
  const draft = ref([])
  let baseline = []
  const watcherUids = computed(() => toValue(report)?.watcherUids || [])
  const canManage = computed(
    () =>
      user.isStaff &&
      user.user?.status?.accountStatus === 'active' &&
      (user.isSuperAdmin || toValue(report)?.submitter?.uid === user.user?.uid)
  )
  function edit() {
    baseline = [...watcherUids.value]
    draft.value = [...baseline]
    editing.value = true
  }
  async function save() {
    if (saving.value) return
    saving.value = true
    try {
      await httpsCallable(
        functions,
        'updateBugReportWatchers'
      )({
        bugReportId: toValue(report).id,
        addUids: draft.value.filter(uid => !baseline.includes(uid)),
        removeUids: baseline.filter(uid => !draft.value.includes(uid)),
      })
      toast.success('Ticket watchers saved.')
      editing.value = false
    } catch (err) {
      toast.error(err.message || 'Could not save ticket watchers.')
    } finally {
      saving.value = false
    }
  }
  return { ...directory, watcherUids, canManage, editing, saving, draft, edit, save }
}
